import { useEffect, useMemo, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { ArrowRight, Loader2 } from "lucide-react";

import { cn, getText } from "shared/lib";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "shared/components";
import { EditorToolbar } from "./EditorToolbar";


export interface EditProposalSummaryDialogProps {
  content: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (content: string) => void;
  submitting?: boolean;
}

export function EditProposalSummaryDialog({
  content,
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: EditProposalSummaryDialogProps) {
  const [isEditable, setIsEditable] = useState(true);

  const extensions = useMemo(() => {
    return [
      StarterKit.configure({
        heading: {
          HTMLAttributes: {
            class: "text-bodylg font-semibold",
          },
          levels: [1],
        },
        paragraph: {
          HTMLAttributes: {
            class: "text-bodylg",
          },
        },
        bulletList: {
          HTMLAttributes: {
            class: "list-disc ml-6",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal ml-6",
          },
        },
        listItem: {
          HTMLAttributes: {
            class: "marker:text-bodysm",
          },
        },
      }),
      Markdown.configure({
        tightLists: true,
        breaks: true,
      }),
    ];
  }, []);

  const editor = useEditor({
    content,
    extensions,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-hidden",
          !isEditable && "text-textSubdued bg-interfaceBgLight",
        ),
      },
      editable: () => isEditable,
    },
    onUpdate({ editor }) {
      setIsEditable(editor.isEditable);
    },
  });

  useEffect(() => {
    if (!editor) return;

    editor.commands.setContent(content);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsEditable(!submitting);
  }, [submitting]);

  const handleClickSubmitAdjustments = () => {
    if (editor) {
      onSubmit(editor.storage.markdown.getMarkdown());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[80%] flex-col gap-6 p-6 md:gap-8 md:p-8"
      >
        <DialogTitle>{getText("Edit proposal")}</DialogTitle>

        <div
          className={cn(
            "border-interactiveBorder focus-within:ring-ring flex flex-col overflow-hidden rounded border focus-within:ring-2 focus-within:ring-offset-2",
            !isEditable && "bg-interfaceBgLight",
          )}
        >
          <EditorToolbar disabled={!isEditable} editor={editor} />
          <div className="flex grow flex-col overflow-hidden">
            <div className="h-full grow overflow-y-auto px-5 py-4">
              <EditorContent editor={editor} />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} variant="outline">
              {getText("Cancel")}
            </Button>
            <Button onClick={handleClickSubmitAdjustments} variant="default">
              {submitting ? <Loader2 className="mr-2 animate-spin" /> : null}
              {getText("Submit and continue")} <ArrowRight />
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EditProposalSummaryDialog;
